import type { ReactNode } from "react";
import type { UiCommandBusV2, UiManagerMainRoute, UiSnapshotV2 } from "@/ui/runtime/contracts/manager-ui-v2";
import { HomeScreen } from "@/ui/screens/manager/home-screen";
import { CompetitionsScreen } from "@/ui/screens/manager/competitions-screen";
import { InboxScreenMain, InboxScreenSecondary, useInboxScreenState } from "@/ui/screens/manager/inbox-screen";
import { SquadScreenMain, SquadScreenSecondary, useSquadScreenState } from "@/ui/screens/manager/squad-screen";
import { FixturesScreenMain, FixturesScreenSecondary, useFixturesScreenState } from "@/ui/screens/manager/fixtures-screen";
import { StaffScreenMain, StaffScreenSecondary, useStaffScreenState } from "@/ui/screens/manager/staff-screen";
import { TransfersScreenMain, TransfersScreenSecondary, useTransfersScreenState } from "@/ui/screens/manager/transfers-screen";

interface ManagerScreenRenderArgs {
    snapshot: UiSnapshotV2;
    commands: UiCommandBusV2;
    state: ManagerScreenStateBag;
}

interface ManagerScreenModule {
    secondaryVisible: boolean;
    renderSecondary(args: ManagerScreenRenderArgs): ReactNode;
    renderMain(args: ManagerScreenRenderArgs): ReactNode;
}

export interface ManagerScreenStateBag {
    inbox: ReturnType<typeof useInboxScreenState>;
    squad: ReturnType<typeof useSquadScreenState>;
    fixtures: ReturnType<typeof useFixturesScreenState>;
    staff: ReturnType<typeof useStaffScreenState>;
    transfers: ReturnType<typeof useTransfersScreenState>;
}

export function useManagerScreenStateBag(snapshot: UiSnapshotV2): ManagerScreenStateBag {
    return {
        inbox: useInboxScreenState(snapshot),
        squad: useSquadScreenState(snapshot),
        fixtures: useFixturesScreenState(snapshot),
        staff: useStaffScreenState(),
        transfers: useTransfersScreenState(snapshot),
    };
}

export const managerScreenRegistry: Record<UiManagerMainRoute, ManagerScreenModule> = {
    "/manager/home": {
        secondaryVisible: false,
        renderSecondary: () => null,
        renderMain: ({ snapshot, commands }) => (
            <HomeScreen snapshot={snapshot} commands={commands} onNavigate={(route) => void commands.navigate(route)} />
        ),
    },
    "/manager/competitions": {
        secondaryVisible: false,
        renderSecondary: () => null,
        renderMain: ({ snapshot }) => <CompetitionsScreen snapshot={snapshot} />,
    },
    "/manager/inbox": {
        secondaryVisible: true,
        renderSecondary: ({ commands, state }) => (
            <InboxScreenSecondary
                state={state.inbox}
                onOpenInboxMessage={(messageId) => void commands.inboxOpen(messageId)}
            />
        ),
        renderMain: ({ snapshot, commands, state }) => (
            <InboxScreenMain commands={commands} state={state.inbox} />
        ),
    },
    "/manager/squad": {
        secondaryVisible: true,
        renderSecondary: ({ commands, state }) => (
            <SquadScreenSecondary state={state.squad} commands={commands} />
        ),
        renderMain: ({ commands, state }) => <SquadScreenMain commands={commands} state={state.squad} />,
    },
    "/manager/fixtures": {
        secondaryVisible: true,
        renderSecondary: ({ state }) => <FixturesScreenSecondary state={state.fixtures} />,
        renderMain: ({ snapshot, state }) => <FixturesScreenMain snapshot={snapshot} state={state.fixtures} />,
    },
    "/manager/staff": {
        secondaryVisible: true,
        renderSecondary: ({ state }) => <StaffScreenSecondary state={state.staff} />,
        renderMain: ({ snapshot, commands, state }) => (
            <StaffScreenMain snapshot={snapshot} commands={commands} state={state.staff} />
        ),
    },
    "/manager/transfers": {
        secondaryVisible: true,
        renderSecondary: ({ commands, state }) => (
            <TransfersScreenSecondary state={state.transfers} commands={commands} />
        ),
        renderMain: ({ commands, state }) => (
            <TransfersScreenMain commands={commands} state={state.transfers} />
        ),
    },
};
